<template>
  <aside
    id="sidebar"
    class="left-menu pos-sidebar"
    :class="{
      'collapsed': isCollapsed,
      'toggled': isMobileOpen
    }"
  >
    <div class="align-items-center bg-top-nav d-flex d-lg-none justify-content-between sidebar-logo">
      <h5 class="offcanvas-title fw-bold text-white">
        <i class="fa-solid fa-bars me-2"/>
        {{$t('Menu')}}
      </h5>
      <button
        class="btn btn-sm d-lg-none text-white"
        @click="$emit('close-mobile')"
      >
        <i class="fa-solid fa-times"></i>
      </button>
    </div>
    <div class="d-lg-block d-none left-menu p-3 text-center">
        <h4 class="mb-1 fw-bold">
          <i class="fa-solid fa-utensils me-2"/>
          {{ $t('POS Mode') }}
        </h4>
        <div class="small opacity-75">
          {{ shopName }}
        </div>
        <div class="opacity-50" style="font-size: 0.65rem; letter-spacing: 1px;">
          {{ $appName }} POS
        </div>
    </div>
    <ul class="pos-menu">
      <li>
        <router-link
          :class="[$route.name === 'pos.table'? 'active': '']"
          to="/pos/table"
          @click="handleLinkClick"
        >
          {{ $t('Tables & Zones') }}
        </router-link>
        <router-link
          :class="[$route.name === 'pos.sessions'? 'active': '']"
          to="/pos/sessions"
          @click="handleLinkClick"
        >
          {{ $t('Service Sessions') }}
        </router-link>
        <!-- deliveryPlatformEnabled จาก API ผ่านแพ็กแล้ว · $hasFeature ให้เมนูหายทันทีตอนแพ็กเปลี่ยนกลางกะ -->
        <router-link
          v-if="deliveryPlatformEnabled && $hasFeature('feature_delivery_platforms')"
          :class="[$route.name === 'pos.platform-orders'? 'active': '']"
          to="/pos/platform-orders"
          @click="handleLinkClick"
        >
          {{ $t('_delivery.Platform Orders') }}
        </router-link>
        <!-- <router-link
          :class="[$route.name === 'pos.reservations'? 'active': '']"
          to="/pos/reservations"
          @click="handleLinkClick"
        >
          {{ $t('Reservations') }}
        </router-link> -->
        <router-link
          :class="[$route.name === 'pos.bills'? 'active': '']"
          to="/pos/bills"
          @click="handleLinkClick"
        >
          {{ $t(`Today's Bills`) }}
        </router-link>
      </li>
    </ul>
    <div class="p-4 text-center mt-auto">
      <div class="d-flex align-items-center justify-content-center gap-2 mb-4">
        <div class="lang-toggle-wrapper">
          <input
            type="radio"
            class="btn-check"
            name="posLang"
            id="langTH"
            @click="changeLanguage('th')"
            v-model="language"
            value="th"
          >
          <label class="btn" for="langTH">TH</label>

          <input
            type="radio"
            class="btn-check"
            name="posLang"
            id="langEN"
            v-model="language"
            value="en"
            @click="changeLanguage('en')"
          >
          <label class="btn" for="langEN">EN</label>
        </div>

        <!-- เครื่องพิมพ์ของเครื่องนี้ (utils/printerSettings.js) — เปิด offcanvas ที่ POSLayout mount ไว้
             ซ่อนบนเบราว์เซอร์ที่พิมพ์ตรงไม่ได้เลย (iPhone/iPad) -->
        <printer-settings-button
          v-if="canDirectPrint"
          btn-class="btn btn-outline-light btn-sm rounded-circle d-inline-flex align-items-center justify-content-center"
          btn-style="width: 38px; height: 38px;"
        />

        <!-- Fullscreen toggle (ซ่อนถ้าเบราว์เซอร์ไม่รองรับ/ไม่อนุญาต) -->
        <button
          v-if="fsSupported"
          type="button"
          class="btn btn-outline-light btn-sm rounded-circle d-inline-flex align-items-center justify-content-center"
          style="width: 38px; height: 38px;"
          :title="isFullscreen ? $t('Exit Fullscreen') : $t('Fullscreen')"
          :aria-label="isFullscreen ? $t('Exit Fullscreen') : $t('Fullscreen')"
          @click="toggleFs"
        >
          <i class="fa-solid" :class="isFullscreen ? 'fa-compress' : 'fa-expand'"></i>
        </button>
      </div>
      <div class="text-secondary small mb-3">
        <i class="fa-solid fa-circle-user fa-2x mb-2 text-light"/>
        <br>
        {{ $t(user.role) }}: <strong class="text-white">{{ user.fullname }}</strong>
      </div>
      <ladda
        text="Exit Shop"
        :btn-class="'btn btn-outline-danger w-100 fw-bold border-2 rounded-3'"
        icon="<i class='fa-solid fa-arrow-right-from-bracket me-2'></i>"
        @click="handleExitShop"
      />
    </div>
    <!-- <div class="h-100 overflow-auto" style="
      display: flex;
      flex-direction: column;
    ">
    </div> -->
  </aside>
</template>

<script>
import Ladda from '@/plugins/Ladda.vue';
import Cookies from 'js-cookie';
import { setLocale } from '@/plugins/i18n';
import { useAuthStore } from '@/stores/auth.store';
import { useShopStore } from '@/stores/shop.store';
import { isFullscreen, isFullscreenSupported, toggleFullscreen, onFullscreenChange } from '@/utils/fullscreen';
import PrinterSettingsButton from '@/components/printer-settings/PrinterSettingsButton.vue';
import { canDirectPrint } from '@/utils/printerSettings';
export default {
  name: "LeftMenu",
  props: {
    isCollapsed: {
      type: Boolean,
      default: false
    },
    isMobileOpen: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      user: useAuthStore().user || { fullname: 'Guest', role: 'Viewer' },
      language: Cookies.get('local'),
      canDirectPrint: canDirectPrint(),
      fsSupported: isFullscreenSupported(),
      isFullscreen: isFullscreen(),
      removeFsListener: null
    }
  },
  components: {
    Ladda,
    PrinterSettingsButton
  },
  computed: {
    shopName() { return useShopStore().name; },
    deliveryPlatformEnabled() { return useShopStore().deliveryPlatformEnabled; }
  },
  mounted() {
    this.removeFsListener = onFullscreenChange(() => {
      this.isFullscreen = isFullscreen();
    });
  },
  beforeUnmount() {
    if (this.removeFsListener) this.removeFsListener();
  },
  methods: {
    async toggleFs() {
      try {
        await toggleFullscreen();
      } catch (e) {
        // บางเบราว์เซอร์บล็อก fullscreen — เงียบไว้
      }
      this.isFullscreen = isFullscreen();
    },
    changeLanguage(lang) {
      this.currentLang = lang;
      setLocale(lang);
    },
    handleLinkClick() {
      // On mobile, close sidebar when a link is clicked
      if (this.isMobileOpen) {
        this.$emit('close-mobile');
      }
    },
    handleExitShop() {
      try {
        let self = this;
        let title = this.$t("Exit POS Mode") + '?';
        let html =  this.$t('Return to the Back Office dashboard.');
        let confirmButtonText =
          '<i class="fa-solid fa-arrow-right-from-bracket me-1"></i>' + this.$t("Exit Shop");
        this.$swal({
          icon: "question",
          iconColor: "#FF7043",
          title: title,
          // text: "You will not be able to undo this action.",
          html: html,
          showCancelButton: true,
          cancelButtonText: this.$t("Cancel"),
          confirmButtonText: confirmButtonText,
          buttonsStyling: false,
          customClass: {
            confirmButton: "btn btn-primary",
            cancelButton: "btn btn-danger me-2",
          },
          focusConfirm: false,
          showLoaderOnConfirm: true,
          reverseButtons: true,
          preConfirm: async function () {
            self.loadingEnterShop = true;
            useShopStore().outShop();
            self.$router.push({ name: 'dashboards' });
          },
        });
      } catch (error) {
      }
    }
  }
}
</script>
